/**
 * useAuth Hook
 *
 * Provides easy access to authentication context
 * from any component
 */

import { useContext } from 'preact/hooks';
import { AuthContext, type AuthContextValue } from '../context/AuthContext';

/**
 * Hook to access authentication state and methods
 *
 * @throws Error if used outside AuthProvider
 * @returns AuthContextValue
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Hook to get user status helpers
 *
 * @returns Object with status check methods
 */
export function useUserStatus() {
  const { user } = useAuth();

  return {
    isRegistered: user?.status === 'registered',
    isPendingApproval: user?.status === 'pending_approval',
    isApprovedPendingPayment: user?.status === 'approved_pending_payment',
    isActive: user?.status === 'active',
    isRejected: user?.status === 'rejected',
    isInactive: user?.status === 'inactive',
    status: user?.status || null,
  };
}
